import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'
import { recentSearchQuery } from '@/queries/recentSearchQuery'
import suggestedSearchQuery from '@/queries/suggestedSearchQuery'

import {
  useSearchAutocomplete,
  type AutocompleteAnalyticsData,
} from './useSearchAutocomplete'

type ActiveDropdown = 'none' | 'autosuggestion' | 'autocomplete'

export interface AutosuggestionAnalyticsData {
  search_suggestions_type: 'recent searches' | 'trending searches'
  search_suggestions: {
    recent_searches: string[]
    trending_searches: string[]
  }
}

interface UseSearchDropdownOptions {
  onSelectSuggestion: (
    suggestion: string,
    analyticsData?: AutocompleteAnalyticsData | AutosuggestionAnalyticsData
  ) => void
  query?: string
}

export const useSearchDropdown = ({
  onSelectSuggestion,
  query = '',
}: UseSearchDropdownOptions) => {
  // Check feature flags internally
  const isAutocompleteFlagEnabled = useFlag(
    FeatureFlags.ENABLE_SEARCH_AUTO_COMPLETE_BOX
  )
  const isAutosuggestionFlagEnabled = useFlag(
    FeatureFlags.ENABLE_SEARCH_AUTO_SUGGESTION_BOX
  )

  const enabledFeatures = useMemo(
    () => ({
      autocomplete: isAutocompleteFlagEnabled,
      autosuggestion: isAutosuggestionFlagEnabled,
    }),
    [isAutocompleteFlagEnabled, isAutosuggestionFlagEnabled]
  )
  const [currentInputValue, setCurrentInputValue] = useState(query)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [selectedAutosuggestionIndex, setSelectedAutosuggestionIndex] =
    useState(-1)
  const [isAutosuggestionOpen, setIsAutosuggestionOpen] = useState(false)
  const shouldOpenOnFocusRef = useRef(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Get user identity for autosuggestion
  const decodedToken = useDecodedAccessToken()
  const accountId = decodedToken?.accountId
  const personId = decodedToken?.personId

  // Fetch recent searches (only when autosuggestion is open)
  const { data: recentSearchData } = recentSearchQuery.useWithOpts(
    [{ limit: 5, accountId, personId }],
    { enabled: enabledFeatures.autosuggestion && isAutosuggestionOpen }
  )

  const recentSearches = useMemo(
    () =>
      recentSearchData?.recentSearches.map((s: { name: string }) => s.name) ||
      [],
    [recentSearchData]
  )

  // Fetch suggested/trending searches (only when autosuggestion is open)
  const { data: suggestedSearchData } = suggestedSearchQuery.useWithOpts(
    [{ limit: 5 }],
    { enabled: enabledFeatures.autosuggestion && isAutosuggestionOpen }
  )

  const trendingSearches = useMemo(
    () =>
      suggestedSearchData?.suggestedSearches.map(
        (s: { query: string }) => s.query
      ) || [],
    [suggestedSearchData]
  )

  // Combined autosuggestions (recent + trending)
  const autosuggestions = useMemo(
    () => [...recentSearches, ...trendingSearches],
    [recentSearches, trendingSearches]
  )

  // Initialize autocomplete hook
  const autocomplete = useSearchAutocomplete({
    onSelectSuggestion: (suggestion, analyticsData) => {
      setIsInputFocused(false)
      onSelectSuggestion(suggestion, analyticsData)
    },
    initialQuery: query,
    enabled: enabledFeatures.autocomplete,
  })

  const { clearAutocomplete } = autocomplete

  // Sync internal input state when the external query is cleared (e.g. via
  // "Clear All" or removing the search filter chip). Without this, currentInputValue
  // would remain stale and re-open the autocomplete dropdown on next focus.
  useEffect(() => {
    setCurrentInputValue(query) // oxlint-disable-line react/set-state-in-effect
    if (!query) {
      clearAutocomplete()
    }
  }, [clearAutocomplete, query])

  // Determine active dropdown based on input length and focus
  const activeDropdown: ActiveDropdown = useMemo(() => {
    if (
      enabledFeatures.autocomplete &&
      currentInputValue.length >= 3 &&
      autocomplete.isAutocompleteOpen
    ) {
      return 'autocomplete'
    }
    if (
      enabledFeatures.autosuggestion &&
      isAutosuggestionOpen &&
      isInputFocused
    ) {
      // If autocomplete is disabled, show autosuggestion for any length
      // If autocomplete is enabled, only show autosuggestion when < 3 chars
      if (!enabledFeatures.autocomplete || currentInputValue.length < 3) {
        return 'autosuggestion'
      }
    }
    return 'none'
  }, [
    enabledFeatures,
    currentInputValue.length,
    autocomplete.isAutocompleteOpen,
    isAutosuggestionOpen,
    isInputFocused,
  ])

  // Handle input value changes and coordinate transitions
  const handleInputChange = useCallback(
    (value: string) => {
      setCurrentInputValue(value)

      // Let autocomplete hook handle its logic
      if (enabledFeatures.autocomplete) {
        autocomplete.handleInputChange(value)
      }

      // Manage autosuggestion visibility
      if (enabledFeatures.autosuggestion) {
        // If autocomplete is disabled, keep autosuggestion open for any length
        // If autocomplete is enabled, only keep autosuggestion open when < 3 chars
        const shouldShowAutosuggestion =
          isInputFocused && (!enabledFeatures.autocomplete || value.length < 3)

        if (shouldShowAutosuggestion) {
          setIsAutosuggestionOpen(true)
          setSelectedAutosuggestionIndex(-1)
        } else {
          setIsAutosuggestionOpen(false)
        }
      }
    },
    [enabledFeatures, autocomplete, isInputFocused]
  )

  // Handle focus - show appropriate dropdown
  const handleFocus = useCallback(() => {
    // Cancel any pending blur timer so it doesn't close the dropdown
    // immediately after this focus event opens it (race condition on mobile reopen)
    if (blurTimerRef.current !== null) {
      clearTimeout(blurTimerRef.current)
      blurTimerRef.current = null
    }

    setIsInputFocused(true)

    // Show autosuggestion if enabled
    // If autocomplete is disabled, show for any length
    // If autocomplete is enabled, only show when < 3 chars
    if (
      enabledFeatures.autosuggestion &&
      (!enabledFeatures.autocomplete || currentInputValue.length < 3)
    ) {
      setIsAutosuggestionOpen(true)
    }

    // Show autocomplete if enabled and input >= 3 chars
    if (enabledFeatures.autocomplete && currentInputValue.length >= 3) {
      autocomplete.handleFocus(currentInputValue)
    }
  }, [enabledFeatures, currentInputValue, autocomplete])

  // Handle blur with delay
  const handleBlur = useCallback(() => {
    // Delay to allow click events on suggestions to fire
    blurTimerRef.current = setTimeout(() => {
      blurTimerRef.current = null
      setIsInputFocused(false)
      setIsAutosuggestionOpen(false)
      setSelectedAutosuggestionIndex(-1)
    }, 200)
  }, [])

  // Handle selecting an autosuggestion
  const handleSelectAutosuggestion = useCallback(
    (suggestion: string, source: 'recent' | 'trending') => {
      setIsAutosuggestionOpen(false)
      setSelectedAutosuggestionIndex(-1)
      setIsInputFocused(false)

      const analyticsData: AutosuggestionAnalyticsData = {
        search_suggestions_type:
          source === 'recent' ? 'recent searches' : 'trending searches',
        search_suggestions: {
          recent_searches: recentSearches,
          trending_searches: trendingSearches,
        },
      }

      onSelectSuggestion(suggestion, analyticsData)
    },
    [recentSearches, trendingSearches, onSelectSuggestion]
  )

  // Handle keyboard navigation for autosuggestion
  const handleAutosuggestionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
      if (!isAutosuggestionOpen || activeDropdown !== 'autosuggestion') return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedAutosuggestionIndex((prev) =>
            prev < autosuggestions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedAutosuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          if (selectedAutosuggestionIndex >= 0) {
            e.preventDefault()
            const suggestion = autosuggestions[selectedAutosuggestionIndex]
            const source =
              selectedAutosuggestionIndex < recentSearches.length
                ? 'recent'
                : 'trending'
            if (suggestion !== undefined) {
              handleSelectAutosuggestion(suggestion, source)
            }
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsAutosuggestionOpen(false)
          setSelectedAutosuggestionIndex(-1)
          break
      }
    },
    [
      isAutosuggestionOpen,
      activeDropdown,
      autosuggestions,
      selectedAutosuggestionIndex,
      recentSearches.length,
      handleSelectAutosuggestion,
    ]
  )

  // Unified keyboard handler that routes to active dropdown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
      if (activeDropdown === 'autocomplete') {
        autocomplete.handleKeyDown(e)
      } else if (activeDropdown === 'autosuggestion') {
        handleAutosuggestionKeyDown(e)
      }
    },
    [activeDropdown, autocomplete, handleAutosuggestionKeyDown]
  )

  // Clear search and reset to autosuggestion if focused
  const clearSearch = useCallback(() => {
    setCurrentInputValue('')
    autocomplete.clearAutocomplete()

    if (enabledFeatures.autosuggestion && isInputFocused) {
      setIsAutosuggestionOpen(true)
      setSelectedAutosuggestionIndex(-1)
    }
  }, [enabledFeatures.autosuggestion, isInputFocused, autocomplete])

  // Close autosuggestion
  const closeAutosuggestion = useCallback(() => {
    setIsAutosuggestionOpen(false)
    setSelectedAutosuggestionIndex(-1)
  }, [])

  // Handle mouse/pointer down for opening autosuggestion
  const handleMouseDown = useCallback(() => {
    shouldOpenOnFocusRef.current = true
    if (enabledFeatures.autosuggestion && currentInputValue.length < 3) {
      setIsAutosuggestionOpen(true)
    }
  }, [enabledFeatures.autosuggestion, currentInputValue.length])

  // Handle Tab key for opening autosuggestion on focus
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
      if (e.key === 'Tab') {
        shouldOpenOnFocusRef.current = true
      }
      // Also handle regular keyboard navigation
      handleKeyDown(e)
    },
    [handleKeyDown]
  )

  return useMemo(
    () => ({
      // Active dropdown state
      activeDropdown,

      // Autosuggestion state
      isAutosuggestionOpen,
      autosuggestions,
      recentSearches,
      trendingSearches,
      selectedAutosuggestionIndex,

      // Autocomplete state (pass-through)
      autocomplete,

      // Current input value
      currentInputValue,

      // Unified handlers
      handleInputChange,
      handleKeyDown: handleInputKeyDown,
      handleFocus,
      handleBlur,
      handleMouseDown,
      handleSelectAutosuggestion,
      clearSearch,
      closeAutosuggestion,

      // Refs for component use
      shouldOpenOnFocusRef,
    }),
    [
      activeDropdown,

      isAutosuggestionOpen,
      autosuggestions,
      recentSearches,
      trendingSearches,
      selectedAutosuggestionIndex,

      autocomplete,

      currentInputValue,

      handleInputChange,
      handleInputKeyDown,
      handleFocus,
      handleBlur,
      handleMouseDown,
      handleSelectAutosuggestion,
      clearSearch,
      closeAutosuggestion,

      shouldOpenOnFocusRef,
    ]
  )
}
